import { motion } from "framer-motion";
import { ShieldCheck, Award, FileCheck, Globe, Download, ExternalLink } from "lucide-react";

const certifications = [
  {
    icon: Award,
    logo: "/images/apeda.svg",
    name: "APEDA Certificate",
    regNo: "Reg. No: 212810",
    validity: "Valid until: 20/10/2026",
    holder: "P.S. IMPEX",
    issuedBy: "Agricultural and Processed Food Products Export Development Authority, Ministry of Commerce & Industry, Govt. of India",
    scope: "All APEDA Scheduled Products — Registered as Merchant Exporter",
    details: [
      "IE Code: MEUPS9724M",
      "Proprietor: Manjot Singh",
      "Date of Issue: 21/10/2021",
    ],
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/nykcorlk_APEDA%20CERTIFICATE.pdf",
  },
  {
    icon: ShieldCheck,
    logo: "/images/fssai.svg",
    name: "FSSAI License",
    regNo: "License No: 12123999000091",
    validity: "Valid until: 01/09/2026",
    holder: "P.S. IMPEX",
    issuedBy: "Food Safety and Standards Authority of India (FSSAI)",
    scope: "Central License — Trade/Retail, Merchant Exporter",
    details: [
      "Category: Central License",
      "Renewal License issued: 19/08/2025",
      "Person in charge: Manjot Singh",
    ],
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/dl56uthj_FSSAI%20CERTIFICATE%20UPTO%202026.pdf",
  },
  {
    icon: FileCheck,
    logo: "/images/iso.svg",
    name: "ISO 9001:2015",
    regNo: "Cert. No: PSF3Q1803V25",
    validity: "Valid until: 17/06/2028",
    holder: "P.S. IMPEX",
    issuedBy: "VRC Certification Pvt. Ltd. — Accredited by EIAC / IAF",
    scope: "Processing, Import, Export & Packaging of Basmati Rice, Wheat Grains and Non-Basmati Rice",
    details: [
      "Date of Issue: 18/06/2025",
      "1st Surveillance Due: 17/06/2026",
      "Verify at: vrccertification.com",
    ],
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/eskj88y7_ISO%20Certificate.pdf",
  },
  {
    icon: Globe,
    logo: "/images/lei.svg",
    name: "LEI Certificate",
    regNo: "LEI: 98450081ED549ABQFD32",
    validity: "Auto-renewal until: 2027",
    holder: "P.S. IMPEX",
    issuedBy: "LEI Register India — Data sourced from GLEIF Database",
    scope: "Legal Entity Identifier for global financial and trade identification",
    details: [
      "Entity: Manjot Singh (Proprietor)",
      "Status: ISSUED",
      "Registered via: legalentityidentifier.in",
    ],
    pdf: "https://customer-assets.emergentagent.com/job_premium-rice-hub-3/artifacts/9u6nbp5d_LEI%20PS%20IMPEX.pdf",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function CertificationsPage() {
  return (
    <div data-testid="certifications-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Compliance & Trust
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-green leading-[0.95] mb-6">
              Our{" "}
              <span className="italic text-brand-gold">Certifications</span>
            </h1>
            <p className="text-brand-muted text-base sm:text-lg font-sans max-w-2xl leading-relaxed">
              P.S.IMPEX holds all necessary certifications and registrations to
              operate as a trusted, compliant rice exporter. View and verify our
              credentials below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certificates Grid */}
      <section data-testid="certificates-grid" className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col gap-16">
            {certifications.map((cert, i) => {
              const Icon = cert.icon;
              return (
                <motion.div
                  key={cert.name}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  className="bg-white rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.06)] border border-brand-gold/10 overflow-hidden"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Certificate Visual Preview */}
                    <a
                      href={cert.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/preview bg-brand-green relative flex flex-col items-center justify-center min-h-[400px] lg:min-h-[500px] p-10 cursor-pointer overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23C5A059%22%20fill-opacity%3D%220.06%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
                      <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="w-24 h-24 rounded-3xl bg-brand-gold/15 flex items-center justify-center mb-6 group-hover/preview:bg-brand-gold/25 transition-colors duration-300">
                          <Icon size={44} className="text-brand-gold" />
                        </div>
                        <h3 className="font-serif text-3xl font-medium text-brand-bone mb-2">
                          {cert.name}
                        </h3>
                        <p className="text-brand-gold text-sm font-sans font-semibold mb-6">
                          {cert.regNo}
                        </p>
                        <div className="flex items-center gap-2 px-6 py-3 bg-brand-gold/20 text-brand-bone text-sm font-sans font-medium rounded-full group-hover/preview:bg-brand-gold/30 transition-colors duration-300">
                          <ExternalLink size={16} />
                          Click to View Certificate
                        </div>
                      </div>
                    </a>

                    {/* Details */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center">
                          <Icon size={22} className="text-brand-gold" />
                        </div>
                        <div>
                          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green">
                            {cert.name}
                          </h2>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 mb-6">
                        <span className="px-3 py-1 bg-brand-gold/10 text-brand-gold text-xs font-sans font-semibold rounded-full">
                          {cert.regNo}
                        </span>
                        <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-sans font-semibold rounded-full">
                          {cert.validity}
                        </span>
                      </div>

                      <div className="mb-6">
                        <p className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium mb-1">
                          Issued By
                        </p>
                        <p className="text-brand-text text-sm font-sans leading-relaxed">
                          {cert.issuedBy}
                        </p>
                      </div>

                      <div className="mb-6">
                        <p className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium mb-1">
                          Scope
                        </p>
                        <p className="text-brand-text text-sm font-sans leading-relaxed">
                          {cert.scope}
                        </p>
                      </div>

                      <div className="mb-8">
                        <p className="text-xs uppercase tracking-wider text-brand-muted font-sans font-medium mb-3">
                          Key Details
                        </p>
                        <div className="flex flex-col gap-2">
                          {cert.details.map((d, j) => (
                            <div key={j} className="flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-1.5 shrink-0" />
                              <span className="text-brand-text text-sm font-sans">
                                {d}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <a
                          href={cert.pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`view-cert-${cert.name.toLowerCase().replace(/\s/g, "-")}`}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-bone text-sm font-sans font-semibold rounded-full hover:bg-brand-gold transition-colors duration-300"
                        >
                          <ExternalLink size={14} />
                          View Full Certificate
                        </a>
                        <a
                          href={cert.pdf}
                          download
                          data-testid={`download-cert-${cert.name.toLowerCase().replace(/\s/g, "-")}`}
                          className="inline-flex items-center gap-2 px-6 py-3 border border-brand-green/20 text-brand-green text-sm font-sans font-medium rounded-full hover:bg-brand-sand transition-colors duration-300"
                        >
                          <Download size={14} />
                          Download PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section data-testid="trust-banner" className="py-24 lg:py-32 bg-brand-green">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Verified & Trusted
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-bone leading-tight mb-6">
              Your Assurance of Quality
            </h2>
            <p className="text-brand-bone/70 text-base font-sans max-w-2xl mx-auto leading-relaxed">
              All certificates are verifiable with their respective issuing
              authorities. P.S.IMPEX maintains full compliance with Indian and
              international food safety and export regulations.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
